import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import {
  PhotoCamera as CameraIcon,
  PhotoLibrary as LibraryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { compressImage } from '../../utils/compressImage';

// Lightweight in-form photo picker. Offers two paths:
//   * "Upload photos" — a plain multiple file input (no `capture`) so mobile
//     browsers open the gallery/photo library and allow selecting MANY images
//     at once (and the file picker on desktop).
//   * "Take photo" — a `capture="environment"` input that opens the rear
//     camera directly for a single shot.
// A single input with both `capture` and `multiple` doesn't work: `capture`
// wins, forcing the camera and disabling gallery multi-select. Hence two
// inputs.
//
// Photos are compressed client-side and held in parent state until the
// wildfire is created — upload happens against the new wildfire ID after
// submit.
//
// Props:
//   photos:   array of { file, preview, originalSize, compressedSize }
//   onChange: (newPhotos) => void  — receives the next array
const PhotoCapture = ({ photos = [], onChange }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setBusy(true);
    setError(null);
    try {
      const compressed = await Promise.all(files.map((f) => compressImage(f)));
      onChange([...photos, ...compressed]);
    } catch (e) {
      console.error('Photo compression failed', e);
      setError('Could not process one of the photos. Try a smaller image.');
    } finally {
      setBusy(false);
      // Allow re-selecting the same file
      event.target.value = '';
    }
  };

  const handleRemove = (index) => {
    const next = photos.slice();
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
        <Button
          variant="contained"
          component="label"
          startIcon={busy ? <CircularProgress size={18} /> : <LibraryIcon />}
          disabled={busy}
        >
          {busy ? 'Processing…' : 'Upload photos'}
          {/* No `capture` attribute → mobile opens the gallery and allows
              selecting multiple images at once. */}
          <input
            type="file"
            accept="image/*"
            hidden
            multiple
            onChange={handleSelect}
          />
        </Button>
        <Button
          variant="outlined"
          component="label"
          startIcon={<CameraIcon />}
          disabled={busy}
        >
          Take photo
          {/* `capture="environment"` opens the rear camera for a single shot. */}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={handleSelect}
          />
        </Button>
        {photos.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {photos.length} photo{photos.length === 1 ? '' : 's'} ready
          </Typography>
        )}
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {photos.length > 0 && (
        <Grid container spacing={1}>
          {photos.map((item, index) => (
            <Grid item xs={6} sm={4} md={3} key={`${item.file.name}-${index}`}>
              <Box sx={{ position: 'relative' }}>
                <img
                  src={item.preview}
                  alt={`Photo ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: 4,
                    display: 'block',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemove(index)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                  }}
                  aria-label="Remove photo"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.compressedSize ? `${item.compressedSize} KB` : ''}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default PhotoCapture;
